import { motion } from "framer-motion";
import { CheckCircle2, Building2, Cog, Layers3 } from "lucide-react";
import { BlueprintMachine } from "@/components/site/DecorativeBG";

const GROUP_COMPANIES = [
  {
    icon: Building2,
    name: "Tirupati Autocomp Industrees",
    body: "Precise engineering component manufacturing set-up.",
  },
  {
    icon: Cog,
    name: "Tirupati Engineering Works",
    body: "Precise engineering component manufacturing set-up.",
  },
  {
    icon: Layers3,
    name: "Tirupati Coters",
    body: "Surface treatment, plating and finishing operations.",
  },
];

const PILLARS = [
  {
    title: "Vertically Integrated Manufacturing",
    body:
      "State-of-the-art in-house capabilities — Multi-Axis CNC Turning Centres, VMC Machining Centres, Forging, Fabrication & Welding, Silver & Copper Plating and Liquid Spray Painting facilities under one roof.",
  },
  {
    title: "Audited Manufacturing Partner Network",
    body:
      "A robust ecosystem of qualified and audited manufacturing partners across India — covering die casting, rubber moulding and complementary commodities at competitive cost structures.",
  },
  {
    title: "Single-Point Sourcing & Supply",
    body:
      "We simplify procurement and supply chain management with reliable solutions, competitive pricing, stringent quality assurance and on-time delivery from prototype to full-scale production.",
  },
];

export default function AboutUs() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 lg:py-32 bg-brand-bg overflow-hidden"
    >
      <BlueprintMachine className="-bottom-6 -left-20 lg:-left-4" opacity={0.32} bounce />
      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <picture>
                <source srcSet="/factory-about.webp" type="image/webp" />
                <img
                  src="/factory-about.jpg"
                  alt="Tirupati Global Sourcing Company manufacturing facility"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-navy/40 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-8 -right-4 lg:-right-10 bg-white border border-gray-200 shadow-xl p-6 w-72">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-orange font-semibold">
                Backed By
              </div>
              <div className="mt-2 font-heading font-extrabold text-2xl text-brand-navy leading-tight">
                Tirupati Group
                <br />
                of Companies
              </div>
              <div className="h-px bg-gray-200 my-4" />
              <div className="text-sm text-brand-text/75 leading-relaxed">
                A diversified manufacturing group with advanced production
                facilities in India.
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="divider-rule" />
              <span className="eyebrow">About Tirupati Global Sourcing Company</span>
            </div>
            <h2 className="h-section">
              Tirupati Global Sourcing Company —
              <br />
              <span className="text-brand-blue">your trusted bridge to India&apos;s manufacturing ecosystem.</span>
            </h2>
            <p className="mt-6 text-brand-text/75 text-base lg:text-lg max-w-2xl leading-relaxed">
              International OEMs face the same recurring challenges sourcing
              from India — language, quality consistency, payment terms and
              logistics complexity. <span className="font-semibold text-brand-navy">Tirupati Global Sourcing Company</span>{" "}
              (TGSC) is the single, accountable partner that removes all of
              them.
            </p>
            <p className="mt-4 text-brand-text/70 text-base max-w-2xl leading-relaxed">
              You share your drawings with TGSC — we route them to either our
              own group factories (<span className="font-semibold text-brand-navy">Tirupati
              Engineering Works · Tirupati Autocomp Industrees · Tirupati
              Coters</span>) or to a pre-audited Indian manufacturing partner.
              Every batch is then inspected in our quality lab and exported
              under one consolidated shipment, fully documented.
            </p>
            <p className="mt-4 text-brand-text/70 text-base max-w-2xl leading-relaxed">
              TGSC operates under{" "}
              <span className="font-semibold text-brand-navy">
                ISO 9001:2015, IATF 16949:2016 and VDA 6.3
              </span>
              {" "}— exporting to{" "}
              <span className="font-semibold text-brand-navy">
                USA, UK, Europe and the Middle East
              </span>
              .
            </p>

            <div className="mt-12 space-y-8">
              {PILLARS.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="flex gap-5"
                  data-testid={`about-pillar-${i}`}
                >
                  <div className="shrink-0">
                    <div className="h-11 w-11 flex items-center justify-center bg-brand-navy text-white">
                      <CheckCircle2 className="h-5 w-5" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-heading font-extrabold text-xl text-brand-navy">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-brand-text/75 leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Group companies showcase */}
            <div className="mt-14">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-blue font-semibold">
                  Group Companies
                </div>
                <a
                  href="https://www.tirupatigroups.co.in"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="group-companies-link"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-brand-navy hover:text-brand-orange transition-colors group"
                >
                  www.tirupatigroups.co.in
                  <span
                    className="inline-flex items-center gap-1 bg-brand-orange text-white px-2.5 py-1 text-[10px] tracking-[0.14em] font-extrabold group-hover:bg-brand-navy transition-colors"
                    data-testid="group-companies-click-here-badge"
                  >
                    Click here
                    <span aria-hidden>↗</span>
                  </span>
                </a>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
                {GROUP_COMPANIES.map((g) => {
                  const Icon = g.icon;
                  return (
                    <div key={g.name} className="bg-white p-5 hover:bg-brand-soft transition-colors">
                      <div className="h-10 w-10 flex items-center justify-center bg-brand-navy text-white">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="mt-4 font-heading font-extrabold text-sm text-brand-navy leading-tight">
                        {g.name}
                      </div>
                      <div className="mt-1.5 text-xs text-brand-text/60 leading-snug">
                        {g.body}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
